/**
 * Camera capture helpers. Stays local. Never persists raw frames.
 */

export interface CameraStream {
  stream: MediaStream;
  track: MediaStreamTrack;
  stop: () => void;
}

export async function openCamera(opts: { facingMode?: 'user' | 'environment'; width?: number; height?: number } = {}): Promise<CameraStream> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: opts.facingMode ?? 'environment',
      width: { ideal: opts.width ?? 640 },
      height: { ideal: opts.height ?? 480 },
    },
    audio: false,
  });
  const track = stream.getVideoTracks()[0];
  if (!track) {
    stream.getTracks().forEach((t) => t.stop());
    throw new Error('no-video-track');
  }
  return {
    stream,
    track,
    stop: () => stream.getTracks().forEach((t) => t.stop()),
  };
}

export async function setTorch(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  const caps = (track.getCapabilities ? track.getCapabilities() : {}) as MediaTrackCapabilities & { torch?: boolean };
  if (!caps.torch) return false;
  try {
    await track.applyConstraints({ advanced: [{ torch: on } as unknown as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}

export function frameToCanvas(video: HTMLVideoElement, canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return ctx;
}

export function meanRedChannel(ctx: CanvasRenderingContext2D): number {
  const img = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  let r = 0;
  const data = img.data;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) r += data[i] ?? 0;
  return r / pixels;
}
